import { MRT_ColumnDef } from "material-react-table";
import { FrontendStatus, SearchNotOrdered } from "./types";
import { Chip, IconButton, Link, Stack, Typography } from "@mui/material";
import { fShortMonthDayYearSlashNotation } from "../utils/formatTime";
import Iconify from "../../minimals/components/iconify";
import { Dispatch, SetStateAction } from "react";
import OpenInHomePageButton from "../searches/OpenInHomePageButton";
import { createAutocompleteColumn } from "../reports/generateAutocompleteColumnAndFilter";
import CustomStatusFilter from "../orders/CustomMultiSelectFilter";
import { ActionNoteButton } from "./ActionNoteButton";

const getStatusColor = (status: FrontendStatus) => {
  switch (status) {
    case "Required":
      return "error";
    case "Due":
      return "error";
    case "Overdue":
      return "error";
    case "Ordered":
      return "warning";
    case "Waiting for Client":
      return "info";
    case "Received":
      return "success";
    case "N/A":
      return "default";
    default:
      return "primary";
  }
};

export const getColumns = ({
  setSelectedEditRowStatus,
  setSelectedEditRowETA
}: {
  setSelectedEditRowStatus: Dispatch<SetStateAction<SearchNotOrdered | null>>;
  setSelectedEditRowETA: Dispatch<SetStateAction<SearchNotOrdered | null>>;
}): MRT_ColumnDef<SearchNotOrdered>[] => [
  {
    id: "notes",
    header: "",
    enableColumnFilter: false,
    enableColumnActions: false,
    enableColumnDragging: false,
    enableColumnFilterModes: false,
    size: 50,
    enableHiding: false
  },
  {
    header: "Order Number",
    accessorKey: "orderNumberWithPrefix",
    filterFn: (row, _, filterValue) => {
      return row.original.orderNumberWithPrefix.toLowerCase().includes(filterValue.toLowerCase());
    },
    Cell: ({ renderedCellValue, row }) => (
      <Stack direction="row" spacing={1} alignItems="center">
        <OpenInHomePageButton orderNumber={row.original.orderNumber} />
        <Link href={`/searches/${row.original.orderNumber}`} target="_blank">
          {renderedCellValue}
        </Link>
      </Stack>
    ),
    muiFilterTextFieldProps: {
      variant: "outlined",
      size: "small"
    }
  },
  {
    header: "Status",
    accessorKey: "frontendStatus",
    size: 230,
    Cell: ({ renderedCellValue, row }) => (
      <Stack direction="row" alignItems="center" spacing={1}>
        <IconButton onClick={() => setSelectedEditRowStatus(row.original)}>
          <Iconify icon={"eva:edit-fill"} />
        </IconButton>
        <Chip
          label={renderedCellValue}
          size="small"
          color={getStatusColor(row.original.frontendStatus)}
          sx={{ borderRadius: 1 }}
          variant="soft"
        />
        {row.original.actionKey && row.original.frontendStatus !== "ETA Confirmed" && (
          <ActionNoteButton entity={row.original} />
        )}
      </Stack>
    ),
    filterVariant: "multi-select",
    filterSelectOptions: [
      { text: "Due", value: "Due" },
      { text: "Overdue", value: "Overdue" },
      { text: "Required", value: "Required" },
      { text: "Awaiting ETA", value: "Awaiting ETA" },
      { text: "ETA Requested", value: "ETA Requested" },
      { text: "Ordered", value: "Ordered" },
      { text: "ETA Upcoming", value: "ETA Upcoming" },
      { text: "ETA Accuracy Requested", value: "ETA Accuracy Requested" },
      { text: "ETA Confirmed", value: "ETA Confirmed" },
      { text: "Received", value: "Received" },
      { text: "Waiting for Client", value: "Waiting for Client" },
      { text: "N/A", value: "N/A" }
    ],
    muiFilterTextFieldProps: {
      variant: "outlined",
      size: "small"
    },
    Filter: CustomStatusFilter
  },
  createAutocompleteColumn<SearchNotOrdered>("Department", "department", {
    placeholder: "Department",
    cellPaddingRight: 3,
    size: 150
  }),
  {
    header: "Assigned To",
    accessorKey: "assignedToUser.fullName",
    Cell: ({ renderedCellValue, row }) =>
      row.original.assignedToEmail ? (
        <Link href={`mailto:${row.original.assignedToEmail}`}>{renderedCellValue}</Link>
      ) : (
        renderedCellValue
      ),
    muiFilterTextFieldProps: {
      variant: "outlined",
      size: "small"
    }
  },
  {
    header: "Days Since Placed",
    accessorKey: "daysSincePlaced",
    Cell: ({ renderedCellValue, row }) => <Chip label={renderedCellValue} size="small" sx={{ borderRadius: 1 }} />,
    muiFilterTextFieldProps: {
      variant: "outlined",
      size: "small"
    }
  },
  {
    header: "Days Since Ordered",
    accessorKey: "daysSinceOrdered",
    Cell: ({ renderedCellValue, row }) =>
      row.original.daysSinceOrdered > 0 ? (
        <Chip label={renderedCellValue} size="small" sx={{ borderRadius: 1 }} />
      ) : null,
    muiFilterTextFieldProps: {
      variant: "outlined",
      size: "small"
    }
  },
  {
    id: "requestDueDate",
    header: "Search Due By",
    accessorFn: (row) => (row.requestDueDate ? fShortMonthDayYearSlashNotation(row.requestDueDate) : ""),
    Cell: ({ renderedCellValue }) => renderedCellValue,
    muiFilterTextFieldProps: {
      variant: "outlined",
      size: "small"
    }
  },
  {
    id: "eta",
    header: "ETA",
    accessorFn: (row) => (row.eta ? fShortMonthDayYearSlashNotation(row.eta) : ""),
    Cell: ({ row, renderedCellValue }) => (
      <Stack direction="row" alignItems="center" spacing={1}>
        <IconButton onClick={() => setSelectedEditRowETA(row.original)}>
          <Iconify icon={"eva:edit-fill"} />
        </IconButton>
        <Typography>{renderedCellValue}</Typography>
      </Stack>
    ),
    muiFilterTextFieldProps: {
      variant: "outlined",
      size: "small"
    }
  },
  {
    header: "Abstractor",
    accessorKey: "abstractorName",
    Cell: ({ renderedCellValue, row }) =>
      row.original.abstractorEmail ? (
        <Link href={`mailto:${row.original.abstractorEmail}?subject=${row.original.emailSubjectLine}`}>
          {renderedCellValue}
        </Link>
      ) : (
        ""
      ),
    muiFilterTextFieldProps: {
      variant: "outlined",
      size: "small"
    }
  }
];
